import { Component } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResourceFclComponent } from '../resource-fcl/resource-fcl.component';
import { ResourceModel } from '../../../models/resources/resource.model';
import { PolicyAction, ResourceType } from '../../../helpers/Const';
import { FileUploadService } from '../../../../service/services/misc/file-upload/file-upload.service';
import { NotificationsService } from '../../../../service/services/misc/notifications/notifications.service';
import { DialogService } from '../../../../service/services/misc/dialog/dialog.service';
import { TablePersonalizationService } from '../../../../service/services/misc/table-personalization/table-personalization.service';
import { SwaService } from '../../../../service/services/misc/swa/swa.service';
import { AppConfigService } from '../../../../service/services/misc/app-config/app-config.service';
import { UisService } from '../../../../service/services/misc/uis/uis.service';
import { Router } from '@angular/router';
import { TablePersonalizationDefinitions } from '../../../helpers/TablePersonalizationDefinitions';
import { ResourceHasContentComponent } from '../../../../features/organizations/dialogs/resource-has-content-dialog/resource-has-content-dialog.component';

@Component({ template: '', standalone: false })
export class AbstractContentComponent extends ResourceFclComponent {

    items: ResourceModel[] = [];
    savedItems: ResourceModel[] = [];
    protected currentAdvancedSearchFilters: any = null;
    protected searchSubscription: Subscription | null = null;
    // Declared as @Input() in subclasses; base methods access via this.*
    advancedSearchOpen = false;
    noDataMessage = '';

    protected cantDeleteFolderWithContentMsg =
        "You can't delete this folder because it contains content that wasn't generated using our app setup quick-start wizard.<br><br>" +
        "Please empty the folder manually before attempting to delete it.";
    protected cantDeleteRGWithContentMsg =
        "The resource group you're trying to delete is not empty.  \n" +
        "Please remove all instances from this resource group before deleting it.";

    constructor(
        fileUploadService: FileUploadService,
        notificationsService: NotificationsService,
        dialogService: DialogService,
        tablePersonalizationService: TablePersonalizationService,
        swaService: SwaService,
        appConfigService: AppConfigService,
        uisService: UisService,
        router: Router
    ) {
        super(fileUploadService, notificationsService, dialogService, tablePersonalizationService, swaService, appConfigService, uisService, router);
    }

    getMixedIntoQueryString(resource: ResourceModel): string {
        const mixedIntoApiVersion = resource.resource?.metadata?.mixedInto?.apiVersion;
        const mixedIntoType = resource.resource?.metadata?.mixedInto?.type;
        if (mixedIntoApiVersion && mixedIntoType) {
            return `?mixedIntoApiVersion=${mixedIntoApiVersion}&mixedIntoType=${mixedIntoType}`;
        }
        return '';
    }

    getGetResourceQueryString(resource: ResourceModel): string {
        let query = '?returnType=model';
        const mixedIntoApiVersion = resource.resource?.metadata?.mixedInto?.apiVersion;
        const mixedIntoType = resource.resource?.metadata?.mixedInto?.type;
        if (mixedIntoApiVersion && mixedIntoType) {
            query += `&mixedIntoApiVersion=${mixedIntoApiVersion}&mixedIntoType=${mixedIntoType}`;
        }
        return query;
    }

    showEditWithYaml(resource: ResourceModel): boolean {
        return (
            resource?.type !== ResourceType.ORGANIZATION &&
            resource?.type !== ResourceType.FOLDER &&
            resource?.type !== ResourceType.RESOURCE_GROUP
        );
    }

    showResourceTitleText(resource: ResourceModel): string {
        return this.showEditWithYaml(resource) ? 'Edit with YAML' : 'Edit';
    }

    isEditable(row: ResourceModel): boolean {
        const rules = row?.rules || [];
        return rules.some(r => r.action === PolicyAction.Update || r.action === PolicyAction.All);
    }

    selectResource(event: Event) {
        this.uisService.sendEvent(this.uisService.UIS_EVENT_TYPES.NAVIGATE_IN_HIERARCHY);
        event.stopPropagation();
    }

    onCancelSearch() {
        this.forkReqSubject.next(1);
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
            this.searchSubscription = null;
            this.loading = false;
            this.policiesLoading = false;
        }
    }

    onClearContent() {
        this.onCancelSearch();
        this.items = [];
        this.currentAdvancedSearchFilters = null;
    }

    handleHasContentDelete(type: ResourceType) {
        const title = 'Cannot Delete ' + (type === ResourceType.FOLDER ? 'Folder' : 'Resource Group');
        const body = type === ResourceType.FOLDER ? this.cantDeleteFolderWithContentMsg : this.cantDeleteRGWithContentMsg;
        this._dialogService.openDialog(ResourceHasContentComponent, { title, msg: body }, undefined, '500px');
    }

    getNoDataMessage(): string {
        if (!this.advancedSearchOpen) {
            return this.noDataMessage;
        }

        const hasActiveFilters = this.currentAdvancedSearchFilters &&
            Object.keys(this.currentAdvancedSearchFilters).some(key => {
                const value = this.currentAdvancedSearchFilters[key];
                return value !== null && value !== undefined && value !== '';
            });

        if (hasActiveFilters) {
            return 'No resources match your search query.';
        }

        return "<div>You haven't selected a resource type yet.</div>" +
            "<br/>" +
            "<div>To get started, select a 'Resource Type' in the filter above. Then add any additional filters to narrow your results. </div>" +
            "<br/>" +
            "<div>Tip: You can also save your filter configuration as a named view for quick access later, or load a previously saved view from the Filtered View menu.</div>";
    }

    protected handleAdvancedSearchOpened() {
        setTimeout(() => {
            this.savedItems = [...this.items];
            this.items = [];
        });
        this.switchTablePersonalization(TablePersonalizationDefinitions.AdvancedSearchTable.key);
    }

    protected handleAdvancedSearchClosed() {
        this.onCancelSearch();
        setTimeout(() => {
            this.items = [...this.savedItems];
            this.savedItems = [];
        });
        this.switchTablePersonalization(TablePersonalizationDefinitions.ResourcesTable.key);
    }
}
